const cities = [
  { name: "Lake Charles", zips: "70601, 70605, 70607, 70611, 70615" },
  { name: "Sulphur", zips: "70663, 70665" },
  { name: "Moss Bluff", zips: "70611" },
  { name: "Westlake", zips: "70669" },
  { name: "DeRidder", zips: "70634" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, office, or plant site in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function LakeCharlesLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Lake Charles &amp; Southwest Louisiana
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Lake Charles is one of America's most concentrated industrial corridors. The petrochemical complexes along the Calcasieu River — including major LNG export facilities, chemical manufacturing plants, and oil refinery operations — employ thousands of engineers, technicians, project managers, and contractors who regularly travel internationally for training, vendor relations, and site inspections. Sasol, Westlake Chemical, Citgo, and dozens of industrial contractors maintain operations in the Lake Charles area, and their personnel often receive international travel assignments with minimal advance notice. A plant turnaround in Saudi Arabia or a process safety conference in the Netherlands can come together in ten days, leaving precious little time for standard government passport processing.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the industrial sector, Lake Charles has built a significant hospitality and gaming economy. The major casino resorts along the lakefront attract regional visitors and employ a substantial workforce with connections to gaming industry events, vendor conferences, and training programs in Macau, Singapore, Las Vegas, and the UK. Many of these employees discover their passports have expired — or they never obtained one — only when an industry trip is confirmed. The hospitality sector's travel timeline is often just as compressed as the industrial one.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              McNeese State University adds an academic dimension to the passport demand picture. Engineering students pursuing international co-op placements, business students attending exchange programs in Europe, and faculty conducting international research all need passport services that can accommodate academic calendars and semester-based travel windows. The university's ties to French-speaking communities in Canada and the Caribbean also create heritage travel for Louisiana-French families in the region.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Southwest Louisiana families — from Sulphur to Moss Bluff to Westlake — also book Caribbean cruises out of Galveston and New Orleans regularly, often discovering passport issues after booking. Retirees along the Calcasieu Lake and Gulf Coast shores maintain active international travel schedules. Fast Passport Center provides all of these travelers with a single reliable solution: expedited, professional passport processing with reserved courier submission, predictable timelines, and no agency appointment required. The nearest regional passport agency is roughly 150 miles away in Houston — we eliminate that drive entirely.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Lake Charles, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Southwest Louisiana &amp; the Calcasieu region</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — serving Lake Charles, Sulphur &amp; all of Southwest LA
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Start online, ship overnight from anywhere in Southwest Louisiana</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, <strong className="text-white">ship your documents overnight</strong> from any UPS or FedEx location in the Lake Charles area. Most Southwest Louisiana clients complete the entire process without leaving home or the plant site.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Southwest Louisiana — start online, ship overnight, and we handle the rest. No need to drive to Houston for a regional agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Louisiana Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Baton Rouge", href: "/cities/baton-rouge" },
                    { name: "Lafayette", href: "/cities/lafayette" },
                    { name: "Shreveport", href: "/cities/shreveport" },
                    { name: "Monroe", href: "/cities/monroe" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Lake Charles Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}